import React from 'react'

import { Box, useMediaQuery } from '@mui/material'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import { useTheme } from '@mui/material/styles'

import { navigate, useLocation } from '@redwoodjs/router'

import { useAuth } from 'src/auth'

interface MenuOption {
  icon: React.ComponentType<any>
  title: string
  url?: string
  onClick?: () => unknown
  /** Treat item as active for icon color + list selection (e.g. Packning subpaths). */
  activeMatch?: (pathname: string) => boolean
  /**
   * Non-admin: when this row is selected, keep icon outline in accent orange and
   * only the inner stroke dark (e.g. Packning box tape line).
   */
  keepAccentOutlineWhenActive?: boolean
}

interface Props {
  list: MenuOption[]
  mobileOpen: boolean
  handleDrawerToggle: () => void
  window?: () => Window
}

const drawerWidth = 254

const AdminPanel = ({
  list,
  window,
  mobileOpen,
  handleDrawerToggle,
}: Props) => {
  const location = useLocation()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('md'))
  const { hasRole } = useAuth()

  const drawer = (
    <>
      <Box sx={{ overflow: 'auto', marginTop: 8 }}>
        <List>
          {list.map((list) => {
            const pathname = location.pathname
            const isActive =
              !!list.url &&
              (pathname === list.url || list.activeMatch?.(pathname) === true)
            const inactiveIconColor = hasRole('admin') ? '#2D3748' : '#F6A237'
            const iconColor = isActive ? '#2D3748' : inactiveIconColor
            const splitAccentOutline =
              list.keepAccentOutlineWhenActive && isActive && !hasRole('admin')
            const resolvedIconColor = splitAccentOutline
              ? inactiveIconColor
              : iconColor
            return (
              <ListItem
                key={list.title}
                onClick={() => {
                  handleDrawerToggle()
                  if (list.url) navigate(list.url)
                  if (list.onClick) list.onClick()
                }}
                button
                selected={isActive}
                sx={{
                  color: 'rgba(0, 0, 0, 0.87)',
                  borderRadius: 2,
                  // '&.Mui-selected': {
                  //   bgcolor: 'pick-a-color',
                  // },
                  //I dont think the below is working. Think it should be Mui-selected and bgcolor
                  '&.active': {
                    backgroundColor: '150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
                  },
                }}
              >
                <Box sx={{ mr: 2, color: resolvedIconColor, display: 'flex' }}>
                  <list.icon
                    color={resolvedIconColor}
                    {...(splitAccentOutline ? { accentColor: '#2D3748' } : {})}
                  />
                </Box>
                {/* <ListItemIcon>{list.icon}</ListItemIcon> */}
                <ListItemText primary={list.title} />
              </ListItem>
            )
          })}
        </List>
      </Box>
    </>
  )

  const container =
    window !== undefined ? () => window().document.body : undefined

  return (
    <>
      <Drawer
        container={container}
        variant={isSmall ? 'temporary' : 'permanent'}
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          '& .MuiDrawer-paper': { width: drawerWidth, border: 0 },
        }}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        {drawer}
      </Drawer>
    </>
  )
}

export default AdminPanel
